<!--
  ProfileModal.vue
  User profile modal with stats and password management

  Features:
  - Display user info (username, email, stats)
  - Change avatar
  - Change password (for users with password)
  - Set password (for OAuth users)

  Props:
  - isOpen: boolean - whether modal is open

  Emits:
  - close: when modal should close
  - profile-updated: when profile is updated
-->

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useProfile, type ProfileResponse } from '@/composables/useProfile'
import PasswordChangeForm from './PasswordChangeForm.vue'
import SetPasswordForm from './SetPasswordForm.vue'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
  'profile-updated': [profile: ProfileResponse]
}>()

const { getProfile, loading, error } = useProfile()

// Profile state
const profile = ref<ProfileResponse | null>(null)
const showPasswordChange = ref(false)
const showSetPassword = ref(false)
const successMessage = ref('')

// Computed values
const winRate = computed(() => {
  if (!profile.value || profile.value.games_played === 0) return 0
  return Math.round((profile.value.games_won / profile.value.games_played) * 100)
})

const hasPassword = computed(() => profile.value?.has_password ?? false)
const isOAuthOnly = computed(() => !hasPassword.value)
const authProviderLabel = computed(() => {
  if (!profile.value) return ''
  switch (profile.value.auth_provider) {
    case 'google':
      return 'Google'
    case 'linked':
      return 'Email + Google'
    default:
      return 'Email'
  }
})

// Load profile when modal opens
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await loadProfile()
    } else {
      // Reset state when closing
      showPasswordChange.value = false
      showSetPassword.value = false
      successMessage.value = ''
    }
  }
)

onMounted(async () => {
  if (props.isOpen) {
    await loadProfile()
  }
})

async function loadProfile() {
  try {
    profile.value = await getProfile()
  } catch {
    // Error is handled in composable
  }
}

function handleClose() {
  emit('close')
}

function handlePasswordChangeSuccess() {
  showPasswordChange.value = false
  successMessage.value = 'Password changed successfully!'
  // Reload profile to update password_changed_at
  loadProfile()
  setTimeout(() => {
    successMessage.value = ''
  }, 3000)
}

function handleSetPasswordSuccess() {
  showSetPassword.value = false
  successMessage.value = 'Password set successfully! You can now log in with email and password.'
  // Reload profile to update has_password
  loadProfile()
  setTimeout(() => {
    successMessage.value = ''
  }, 5000)
}

function handleBackdropClick(event: MouseEvent) {
  if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
    handleClose()
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modal-backdrop"
      @click="handleBackdropClick"
      @keydown.esc="handleClose"
    >
      <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <!-- Header -->
        <div class="modal-header">
          <h2 id="profile-title">Profile</h2>
          <button class="close-btn" @click="handleClose" aria-label="Close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Content -->
        <div class="modal-content">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Loading profile...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <span class="material-symbols-outlined">error</span>
            <p>{{ error }}</p>
            <button class="btn-secondary" @click="loadProfile">Try Again</button>
          </div>

          <!-- Profile Content -->
          <template v-else-if="profile">
            <!-- Success Message -->
            <div v-if="successMessage" class="success-message">
              <span class="material-symbols-outlined">check_circle</span>
              {{ successMessage }}
            </div>

            <!-- User Info Section -->
            <div class="profile-header">
              <div class="avatar-section">
                <div class="avatar">
                  <span class="material-symbols-outlined">person</span>
                </div>
              </div>
              <div class="user-info">
                <h3 class="username">{{ profile.username }}</h3>
                <p class="email">{{ profile.email }}</p>
                <span class="auth-badge">
                  <span class="material-symbols-outlined">
                    {{ profile.auth_provider === 'google' || profile.auth_provider === 'linked' ? 'verified' : 'mail' }}
                  </span>
                  {{ authProviderLabel }}
                </span>
              </div>
            </div>

            <!-- Stats Section -->
            <div class="stats-section">
              <div class="stat-card">
                <span class="stat-value">{{ profile.games_played }}</span>
                <span class="stat-label">Games Played</span>
              </div>
              <div class="stat-card">
                <span class="stat-value">{{ profile.games_won }}</span>
                <span class="stat-label">Wins</span>
              </div>
              <div class="stat-card">
                <span class="stat-value">{{ winRate }}%</span>
                <span class="stat-label">Win Rate</span>
              </div>
            </div>

            <!-- Password Section -->
            <div class="password-section">
              <h4>Password</h4>

              <!-- Password Change Form (for users with password) -->
              <PasswordChangeForm
                v-if="showPasswordChange"
                @success="handlePasswordChangeSuccess"
                @cancel="showPasswordChange = false"
              />

              <!-- Set Password Form (for OAuth users) -->
              <SetPasswordForm
                v-else-if="showSetPassword"
                @success="handleSetPasswordSuccess"
                @cancel="showSetPassword = false"
              />

              <!-- Password Actions -->
              <div v-else class="password-actions">
                <template v-if="hasPassword">
                  <p class="password-info">
                    <span class="material-symbols-outlined">lock</span>
                    Password is set
                    <span v-if="profile.password_changed_at" class="last-changed">
                      (last changed {{ formatDate(profile.password_changed_at) }})
                    </span>
                  </p>
                  <button class="btn-secondary" @click="showPasswordChange = true">
                    Change Password
                  </button>
                </template>

                <template v-else>
                  <p class="password-info oauth-notice">
                    <span class="material-symbols-outlined">info</span>
                    You're signed in with Google. Add a password for backup access.
                  </p>
                  <button class="btn-primary" @click="showSetPassword = true">
                    Set Password
                  </button>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
// Helper function (not reactive)
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString()
}

export { formatDate }
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.modal-container {
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.98) 0%, rgba(26, 47, 63, 0.98) 100%);
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}

.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #fff;
}

.modal-content {
  padding: 1.5rem;
}

/* Loading & Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #d4a72c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state .material-symbols-outlined {
  font-size: 2.5rem;
  color: #ef4444;
}

/* Success Message */
.success-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(16, 185, 129, 0.15);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: #10b981;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.success-message .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Profile Header */
.profile-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.avatar-section {
  flex-shrink: 0;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4a72c 0%, #b58e1d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar .material-symbols-outlined {
  font-size: 2rem;
  color: #1a2f3f;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.username {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #fff;
}

.email {
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.auth-badge .material-symbols-outlined {
  font-size: 0.875rem;
  color: #10b981;
}

/* Stats Section */
.stats-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #d4a72c;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Password Section */
.password-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
}

.password-section h4 {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.password-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.password-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.password-info .material-symbols-outlined {
  font-size: 1.125rem;
  color: #10b981;
}

.password-info.oauth-notice .material-symbols-outlined {
  color: #d4a72c;
}

.last-changed {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Buttons */
.btn-primary,
.btn-secondary {
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #d4a72c 0%, #b58e1d 100%);
  border: none;
  color: #1a2f3f;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #e5b933 0%, #c99f2e 100%);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

/* Responsive */
@media (max-width: 480px) {
  .modal-container {
    max-height: 100vh;
    border-radius: 0;
  }

  .stats-section {
    grid-template-columns: 1fr;
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
  }

  .avatar-section {
    display: flex;
    justify-content: center;
  }
}
</style>
